import React, { ReactNode, useMemo } from 'react'
import { Flex } from '@chakra-ui/react'
import { ForgeComponent } from '~core/forge.type'
import { useUserAbilities } from '~editor/hooks/useUser'
import { forgeComponentSubject } from '~editor/utils/abilities'

interface PanelComponentDecoratorProps {
  children: ReactNode
  component: ForgeComponent
}

const PanelComponentDecorator: React.FC<PanelComponentDecoratorProps> = ({
  children,
  component,
}) => {
  const abilities = useUserAbilities()
  const canAddComponent = useMemo(() => {
    return abilities?.can('add', forgeComponentSubject(component))
  }, [abilities, component])

  return (
    <Flex
      justifyContent="center"
      alignItems="center"
      h="100%"
      w="100%"
      borderRadius="10px"
      boxShadow="0px 5px 15px rgba(0, 0, 0, 0.25)"
      background={`${
        canAddComponent
          ? ''
          : 'linear-gradient(0deg, rgba(255, 121, 71, 0.05), rgba(255, 121, 71, 0.05)), '
      } #13151c`}
      backgroundSize="cover"
      backgroundRepeat="no-repeat"
      backgroundPosition="center"
      cursor="pointer"
      position="relative"
      overflow="hidden"
      _before={{
        zIndex: 1,
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        border: '1px solid rgba(0, 0, 0, 0)',
        borderColor: canAddComponent
          ? 'rgba(73, 230, 155, 0)'
          : 'rgba(255, 121, 71, 0.5)',
        borderRadius: 'inherit',
        pointerEvents: 'none',
      }}
      _hover={{
        background: canAddComponent
          ? 'linear-gradient(0deg, rgba(73, 230, 155, 0.1), rgba(73, 230, 155, 0.1)), #13151C'
          : 'linear-gradient(0deg, rgba(255, 121, 71, 0.05), rgba(255, 121, 71, 0.05)), #13151C',
        _before: {
          background: !canAddComponent && 'rgba(19, 21, 28, 0.95)',
          borderColor: canAddComponent
            ? 'rgba(73, 230, 155, 1)'
            : 'rgba(255, 121, 71, 0.5)',
        },
        _after: {
          display: canAddComponent && 'none',
          backdropFilter: 'blur(10px)',
          pointerEvents: 'none',
          content: '"Upgrade to Unlock"',
          zIndex: 2,
          whiteSpace: 'nowrap',
          padding: '13px 17px',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          fontWeight: 700,
          color: '#FFF',
          background:
            'linear-gradient(270deg, rgba(255, 179, 71, 0.2) 0%, rgba(255, 89, 71, 0.2) 100%)',
          border: '1px solid #FF8B47',
          boxShadow: '0px 4px 15px rgba(255, 139, 71, 0.25)',
          borderRadius: '5px',
        },
      }}
    >
      {children}
      <Flex
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        alignItems="center"
        justifyContent="center"
        padding="3px"
        fontSize="10px"
        background="rgba(0,0,0,0.5)"
      >
        {component.displayName}
      </Flex>
    </Flex>
  )
}

export default PanelComponentDecorator
